import * as chrono from "chrono-node"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { FieldValues, useFormContext } from "react-hook-form"

import debounce from "@utils/debounce"
import formatDate from "@utils/formatDate"
import isValidDate from "@utils/isValidDate"

import DatePicker from "@molecules/DatePicker/DatePicker"
import { FormField } from "@molecules/Form/Form"
import Input from "@molecules/Form/inputs/Input/Input"
import { InputName, InputProps } from "@molecules/Form/inputs/Input/Input.types"

import { INPUT_DEBOUNCE_SUBMIT_TIME_MS } from "@constants/input"

import { DateInputProps } from "./DateInput.types"

function DateInput<T extends InputName = InputName>(
    props: DateInputProps & InputProps<T>,
    ref: React.Ref<HTMLInputElement>,
) {
    const { onDateChange, withDatePicker = false, datePickerProps, ...rest } = props

    const inputRef = useRef<HTMLInputElement>(null)
    const hasOpenedRef = useRef(false)
    const form = useFormContext<FieldValues>()
    const fieldName = String(props.name)

    // Initialize the DatePicker's selected date from the form's current value
    const initialFormValue = form.getValues(props.name)
    const [parsedDateForPicker, setParsedDateForPicker] = useState<Date | undefined>(
        isValidDate(initialFormValue) ? initialFormValue : undefined,
    )

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])

    const handleChange = useCallback(
        debounce((e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value

            const results = chrono.parse(value)
            let parsed: Date | null = null

            if (results.length > 0) {
                const result = results[0]
                parsed = result.start.date()

                // If year wasn't explicitly typed, use the current year
                if (!result.start.isCertain("year")) {
                    parsed.setFullYear(new Date().getFullYear())
                }
            }

            onDateChange?.(parsed, value)

            // Update the DatePicker's selected date when typing (or clear it)
            if (withDatePicker) {
                setParsedDateForPicker(parsed ?? undefined)
            }
        }, INPUT_DEBOUNCE_SUBMIT_TIME_MS),
        [onDateChange, withDatePicker],
    )

    const handleClear = useCallback(() => {
        onDateChange?.(null, "")
        if (withDatePicker) {
            setParsedDateForPicker(undefined)
        }
    }, [onDateChange, withDatePicker])

    const inputNode = (
        <Input
            ref={inputRef}
            {...rest}
            onChange={handleChange}
            onClear={handleClear}
            type="text"
            placeholder="Enter date..."
            isClearable={true}
            isTabbable={!withDatePicker}
            size="md"
            isControlled={false}
        />
    )

    if (withDatePicker) {
        const handlePickerSelect = (date: Date | undefined) => {
            const rawValue = date
                ? formatDate({
                      datetime: date,
                      addYear: date.getFullYear() !== new Date().getFullYear(),
                  }).localDateString
                : ""
            onDateChange?.(date ?? null, rawValue)
            setParsedDateForPicker(date)
            void form.trigger(fieldName)
            if (inputRef.current) {
                inputRef.current.value = rawValue
            }
        }

        return (
            <DatePicker
                {...datePickerProps}
                onSelect={handlePickerSelect}
                defaultMonth={parsedDateForPicker}
                selected={parsedDateForPicker}
                onOpenChange={(open) => {
                    datePickerProps?.onOpenChange?.(open)

                    if (open) {
                        hasOpenedRef.current = true
                    }

                    if (!open && hasOpenedRef.current) {
                        // defer to avoid update depth loops
                        setTimeout(() => {
                            // Mark as touched and validate when the picker closes
                            form.setValue(fieldName, form.getValues(fieldName), { shouldTouch: true })
                            void form.trigger(fieldName)
                        }, 0)
                    }
                }}
                data-prevent-input-validation="true"
            >
                {inputNode}
            </DatePicker>
        )
    } else {
        return inputNode
    }
}

const withController = <T extends InputName>(InputComponent: React.FC<DateInputProps & InputProps<T>>) => {
    const Controller = forwardRef<HTMLInputElement, DateInputProps & InputProps<T>>((props, ref) => {
        const { isControlled = true, onDateChange, ...rest } = props
        const form = useFormContext<FieldValues>()
        const controlledFieldName = String(props.name)

        const inputRef = useRef<HTMLInputElement>(null)

        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [])

        if (isControlled) {
            return (
                <FormField
                    control={form.control}
                    name={props.name}
                    render={({ field, fieldState }) => {
                        const lastUpdateSource = useRef<"user" | "form">("form")

                        const handleDateChange = (parsedDate: Date | null, rawValue: string) => {
                            onDateChange?.(parsedDate, rawValue)
                            lastUpdateSource.current = "user"
                            form.setValue(controlledFieldName, parsedDate, {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true,
                            })
                        }

                        useEffect(() => {
                            if (lastUpdateSource.current === "form") {
                                if (isValidDate(field.value as Date | string | number | null)) {
                                    if (inputRef.current) {
                                        inputRef.current.value = formatDate({
                                            datetime: field.value as unknown as Date,
                                            addYear:
                                                new Date(
                                                    field.value as unknown as string | number | Date,
                                                ).getFullYear() !== new Date().getFullYear(),
                                        }).localDateString
                                    }
                                } else {
                                    if (inputRef.current) {
                                        inputRef.current.value = String((field.value as unknown as string) ?? "")
                                    }
                                }
                            }

                            lastUpdateSource.current = "form"
                        }, [field.value])

                        return (
                            <InputComponent
                                {...rest}
                                onDateChange={handleDateChange}
                                value={undefined}
                                inputHelpText={
                                    fieldState.error?.message
                                        ? {
                                              message: String(fieldState.error.message),
                                              status: "error",
                                              showIcon: true,
                                          }
                                        : rest.inputHelpText
                                }
                                onBlur={() => {
                                    field.onBlur()
                                    form.setValue(controlledFieldName, field.value, { shouldTouch: true })
                                }}
                                ref={inputRef}
                            />
                        )
                    }}
                />
            )
        } else {
            return <InputComponent {...rest} onDateChange={onDateChange} ref={ref} />
        }
    })

    Controller.displayName = "Input"

    return Controller as typeof DateInput
}

export default withController(forwardRef(DateInput))
